import Layout from '@/components/feature/Layout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';
import { products } from '@/mocks/products';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Soldagem de Geomembrana com Cunha Quente',
  description:
    'Entenda o que é a soldagem por cunha quente, como funciona, vantagens, limitações, materiais compatíveis e os equipamentos disponíveis.',
  url: 'https://maquinadesoldageomembrana.com.br/cunha-quente-geomembrana',
  inLanguage: 'pt-BR',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O que é soldagem por cunha quente?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'É um método de termofusão que usa uma cunha metálica aquecida para fundir simultaneamente duas folhas de geomembrana, criando uma solda dupla com canal de teste de pressão.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quais as vantagens da solda por cunha quente?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Alta produtividade, solda dupla com canal de teste, resultado uniforme e conformidade com a norma NBR 16199. É o método mais rápido para grandes áreas planas.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quais materiais podem ser soldados com cunha quente?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'PEAD, PVC, LLDPE (PELBD) e PP, conforme a compatibilidade do equipamento. A maioria dos modelos atende PEAD, PVC e LLDPE.',
      },
    },
  ],
};

const faq = [
  {
    question: 'O que é soldagem por cunha quente?',
    answer:
      'É um método de termofusão que usa uma cunha metálica aquecida para fundir simultaneamente duas folhas de geomembrana, criando uma solda dupla com canal de teste de pressão.',
  },
  {
    question: 'Quais as vantagens da solda por cunha quente?',
    answer:
      'Alta produtividade, solda dupla com canal de teste, resultado uniforme e conformidade com a norma NBR 16199. É o método mais rápido para grandes áreas planas.',
  },
  {
    question: 'Quais materiais podem ser soldados com cunha quente?',
    answer:
      'PEAD, PVC, LLDPE (PELBD) e PP, conforme a compatibilidade do equipamento. A maioria dos modelos atende PEAD, PVC e LLDPE.',
  },
];

export default function CunhaQuenteGeomembranaPage() {
  const cunhaQuente = products.filter((p) => p.category === 'Cunha Quente');

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Cunha Quente' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Soldagem de Geomembrana com Cunha Quente
            </h1>

            <div className="space-y-5 text-foreground-600 leading-relaxed">
              <p>
                A <strong className="text-foreground-950">soldagem por cunha quente</strong> (hot wedge welding) é o método mais utilizado na impermeabilização de grandes áreas. Uma cunha metálica aquecida penetra entre as duas folhas sobrepostas, fundindo-as simultaneamente, enquanto rolos de pressão consolidam a emenda.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Como Funciona</h2>
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 my-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">1</span><span>A cunha metálica é aquecida até a temperatura de fusão do material (280–450°C).</span></li>
                  <li className="flex items-start gap-3"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">2</span><span>A máquina avança sobre a sobreposição, fundindo as duas folhas.</span></li>
                  <li className="flex items-start gap-3"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">3</span><span>Rolos de pressão consolidam a fusão, formando uma solda dupla com canal central.</span></li>
                  <li className="flex items-start gap-3"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">4</span><span>O canal central permite teste de pressão de ar para validar a estanqueidade.</span></li>
                </ul>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Vantagens</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Alta produtividade</strong> — velocidades de 5 a 9 m/min.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Solda dupla com canal de teste</strong> — permite verificar a estanqueidade da emenda.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Resultado uniforme</strong> — parâmetros controlados automaticamente.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Conformidade normativa</strong> — atende à norma NBR 16199.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Limitações</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-close-line text-accent-500 mt-0.5 flex-shrink-0" /><span>Indicada principalmente para <strong>áreas planas</strong> e emendas longas e retas.</span></li>
                <li className="flex items-start gap-2"><i className="ri-close-line text-accent-500 mt-0.5 flex-shrink-0" /><span>Dificuldade em <strong>cantos, junções e geometrias complexas</strong> — nesses casos, a extrusora é mais adequada.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Materiais Compatíveis</h2>
              <p>As máquinas de cunha quente soldam <strong>PEAD, PVC, LLDPE (PELBD) e PP</strong>, conforme a compatibilidade de cada modelo. A maioria dos equipamentos da linha atende PEAD, PVC e LLDPE.</p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Equipamentos de Cunha Quente Disponíveis</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
                {cunhaQuente.map((p) => (
                  <div key={p.id} className="flex items-center justify-between bg-background-100 rounded-xl border border-background-200/70 p-4">
                    <div>
                      <p className="text-foreground-900 text-sm font-bold">{p.name}</p>
                      <p className="text-foreground-500 text-xs">{p.specs.potencia}</p>
                    </div>
                    <span className="text-foreground-400 text-xs whitespace-nowrap">{p.specs.velocidade}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5">Perguntas Frequentes</h2>
              <FaqAccordion items={faq} />
            </div>

            <RelatedLinks
              links={[
                { to: '/maquina-de-solda-geomembrana-automatica', label: 'Máquina Automática' },
                { to: '/extrusora-geomembrana', label: 'Extrusora' },
                { to: '/soldagem-de-geomembrana', label: 'Técnicas de Soldagem' },
                { to: '/modelos-de-maquinas', label: 'Comparativo de Modelos' },
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}